'use client';

import { LocaleLink } from '@/core/i18n';
import { ArrowRight, MapPin, Building2 } from 'lucide-react';
import { cn } from '@/core/utils/cn';
import { usePageContent } from '@/core/i18n/content';
import { TEMPLATE_CHROME } from '@/core/i18n/content/template-chrome.content';
import { CITIES_HUB_CONTENT } from '@/core/i18n/content/cities-hub.content';
import type { CitiesHubContent } from '@/core/i18n/content/cities-hub.content';
import type { TemplateChromeContent } from '@/core/i18n/content/template-chrome.types';
import { useTranslation } from '@/core/i18n';

/* -------------------------------------------------------------------------- */
/*  Data                                                                      */
/* -------------------------------------------------------------------------- */

interface CityEntry {
  slug: string;
  hotels: string;
}

const CITIES: CityEntry[] = [
  { slug: 'hotel-software-dubai', hotels: '5,000+' },
  { slug: 'hotel-software-bangkok', hotels: '8,000+' },
  { slug: 'hotel-software-london', hotels: '10,000+' },
  { slug: 'hotel-software-new-york', hotels: '15,000+' },
  { slug: 'hotel-software-singapore', hotels: '2,000+' },
  { slug: 'hotel-software-paris', hotels: '8,000+' },
  { slug: 'hotel-software-tokyo', hotels: '10,000+' },
  { slug: 'hotel-software-istanbul', hotels: '5,000+' },
  { slug: 'hotel-software-sydney', hotels: '3,000+' },
  { slug: 'hotel-software-los-angeles', hotels: '5,000+' },
];

const REGION_POSITIONS = [
  { key: 'americas' as const, position: 'left-[15%] top-[40%]' },
  { key: 'europe' as const, position: 'left-[45%] top-[25%]' },
  { key: 'middleEast' as const, position: 'left-[55%] top-[45%]' },
  { key: 'asiaPacific' as const, position: 'left-[75%] top-[35%]' },
  { key: 'oceania' as const, position: 'left-[80%] top-[65%]' },
];

/* -------------------------------------------------------------------------- */
/*  Hero                                                                      */
/* -------------------------------------------------------------------------- */

function Hero({ c }: { c: CitiesHubContent }) {
  const { t } = useTranslation();
  return (
    <section id="cities-hub-hero" className="relative w-full overflow-hidden bg-hms-bg">
      <div
        className="pointer-events-none absolute inset-0"
        style={{ background: 'radial-gradient(ellipse 80% 60% at 50% 30%, rgba(200,145,58,0.08) 0%, transparent 70%)' }}
      />
      <div className="relative mx-auto max-w-4xl px-4 pb-16 pt-24 text-center sm:px-6 sm:pt-32 lg:px-8 lg:pt-40">
        <span className="mb-4 inline-flex items-center gap-2 rounded-full border border-hms-gold/20 bg-hms-gold/5 px-4 py-1.5 text-xs font-semibold tracking-wide text-hms-gold">
          <MapPin className="h-3.5 w-3.5" />
          {c.badge}
        </span>
        <h1
          id="cities-hub-title"
          className={cn(
            'font-poppins text-4xl font-extrabold leading-tight tracking-tight sm:text-5xl lg:text-6xl',
            'bg-gradient-to-r from-hms-gold via-hms-goldLt to-amber-400 bg-clip-text text-transparent',
          )}
        >
          {c.title}
        </h1>
        <p className="mx-auto mt-6 max-w-2xl text-base text-hms-textSub sm:text-lg">
          {c.subtitle}
        </p>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*  World Map Visual                                                          */
/* -------------------------------------------------------------------------- */

function WorldMapVisual({ c }: { c: CitiesHubContent }) {
  return (
    <section
      id="cities-hub-map"
      className="mx-auto max-w-6xl px-4 pb-12 sm:px-6 lg:px-8"
    >
      <div className="relative h-48 overflow-hidden rounded-xl border border-hms-border bg-hms-card sm:h-56 lg:h-64">
        <div
          className="pointer-events-none absolute inset-0"
          style={{ background: 'radial-gradient(circle at 50% 50%, rgba(37,99,235,0.06) 0%, transparent 60%)' }}
        />
        {/* Grid lines for map aesthetic */}
        <div className="absolute inset-0 opacity-[0.03]" style={{
          backgroundImage: 'linear-gradient(rgba(232,238,248,1) 1px, transparent 1px), linear-gradient(90deg, rgba(232,238,248,1) 1px, transparent 1px)',
          backgroundSize: '60px 60px',
        }} />
        {/* Region labels */}
        {REGION_POSITIONS.map((r) => (
          <div
            key={r.key}
            className={cn('absolute hidden sm:flex items-center gap-1.5', r.position)}
          >
            <span className="h-2 w-2 rounded-full bg-hms-gold/60" />
            <span className="text-xs font-medium text-hms-textDim">{c.regions[r.key]}</span>
          </div>
        ))}
        {/* Center label */}
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center">
            <p className="text-sm font-medium text-hms-textSub">{c.map.globalCoverage}</p>
            <p className="mt-1 text-2xl font-bold text-hms-text sm:text-3xl">{c.map.citiesCount}</p>
            <p className="mt-1 text-xs text-hms-textDim">{c.map.hotelsServed}</p>
          </div>
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*  City Card                                                                 */
/* -------------------------------------------------------------------------- */

function CityCard({
  city,
  c,
  t,
}: {
  city: CityEntry;
  c: CitiesHubContent;
  t: TemplateChromeContent;
}) {
  const translated = c.cities[city.slug];
  return (
    <LocaleLink
      id={`cities-hub-card-${city.slug}`}
      href={`/${city.slug}`}
      className={cn(
        'group flex flex-col rounded-xl border border-hms-border bg-hms-card p-6',
        'transition-all duration-200 hover:border-hms-gold/30 hover:shadow-lg hover:shadow-hms-gold/5',
      )}
    >
      <div className="mb-3">
        <h3
          id={`cities-hub-name-${city.slug}`}
          className="text-xl font-bold text-hms-text"
        >
          {translated.city}
        </h3>
        <p className="mt-0.5 text-sm text-hms-textSub">{translated.country}</p>
      </div>

      <div className="mb-4 flex items-center gap-2">
        <Building2 className="h-4 w-4 text-hms-gold" />
        <span className="text-sm font-medium text-hms-textSub">
          <span className="text-hms-text">{city.hotels}</span> {c.hotelsLabel}
        </span>
      </div>

      <span className="mt-auto inline-flex items-center gap-1.5 text-sm font-medium text-hms-gold transition-colors group-hover:text-hms-goldLt">
        {t.common.explore}
        <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
      </span>
    </LocaleLink>
  );
}

/* -------------------------------------------------------------------------- */
/*  Main Component                                                            */
/* -------------------------------------------------------------------------- */

export function CitiesHub() {
  const t = usePageContent(TEMPLATE_CHROME);
  const c = usePageContent(CITIES_HUB_CONTENT);
  return (
    <div id="cities-hub-root" className="min-h-screen bg-hms-bg">
      <Hero c={c} />
      <WorldMapVisual c={c} />

      {/* City Grid */}
      <section className="mx-auto max-w-6xl px-4 pb-12 sm:px-6 lg:px-8">
        <div
          id="cities-hub-grid"
          className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3"
        >
          {CITIES.map((city) => (
            <CityCard key={city.slug} city={city} c={c} t={t} />
          ))}
        </div>
      </section>

      {/* CTA */}
      <section
        id="cities-hub-cta"
        className="mx-auto max-w-4xl px-4 pb-24 pt-8 text-center sm:px-6 lg:px-8"
      >
        <div className="rounded-xl border border-hms-border bg-hms-card p-8 sm:p-12">
          <h2 className="text-2xl font-bold text-hms-text sm:text-3xl">
            {c.cta.title}
          </h2>
          <p className="mx-auto mt-3 max-w-xl text-sm text-hms-textSub sm:text-base">
            {c.cta.subtitle}
          </p>
          <LocaleLink
            id="cities-hub-cta-contact"
            href="/contact"
            className={cn(
              'mt-6 inline-flex items-center gap-2 rounded-lg bg-hms-gold px-8 py-3.5',
              'text-sm font-semibold tracking-wide text-hms-bg shadow-lg shadow-hms-gold/20',
              'transition-all duration-200 hover:bg-hms-goldLt hover:shadow-hms-gold/30 active:scale-[0.98]',
            )}
          >
            {t.common.contactUs}
            <ArrowRight className="h-4 w-4" />
          </LocaleLink>
        </div>
      </section>
    </div>
  );
}
